/**
 * Decision Room → CRM panel, seller-only (2026-09-07, flag `crm_depth`).
 *
 * One deal per room. The panel suggests deals matched from the buyer
 * emails on the People tab and the primary contact, or the seller
 * searches by name and picks one. Once linked it shows the deal's
 * stage, status and value as the CRM sees it (refreshed by the resync
 * worker every 30 minutes), the stage history TrailerCast has recorded,
 * and the deal's CRM contacts — with a one-click "Add to People" for
 * anyone who isn't on the tab yet.
 *
 * Every record the panel names links back into the CRM's own UI when
 * the server hands us a URL (2026-09-07) — the deal, the deals offered
 * as suggestions or search hits, and each contact. The point is to stop
 * a rep bouncing between systems to answer "is this the right deal".
 * The URL is always built server-side by the provider's adapter; a
 * connection that can't produce one (a HubSpot connection made before
 * the portal id was stored) simply renders plain text, never a link
 * that goes nowhere.
 *
 * Unlink is soft on the server: history survives, and re-linking
 * creates a fresh link. Nothing here writes a stage back INTO the CRM.
 *
 * Backend: routes/buyerSpaceCrm.js (mounted under
 * /api/buyer-spaces/:id/crm, same room access gate as the room).
 */

import { useCallback, useEffect, useState } from 'react';
import { apiRequest, formatWhen, moneyLabel } from '../utils/apiRequest';

const STATUS_CHIP = {
  won:     { bg: '#ecfdf5', ink: '#065f46', label: 'Won' },
  lost:    { bg: '#fef2f2', ink: '#991b1b', label: 'Lost' },
  pending: { bg: '#eff6ff', ink: '#1e40af', label: 'Open' },
};

function DealStatus({ status }) {
  const s = STATUS_CHIP[status] || STATUS_CHIP.pending;
  return <span style={{ fontSize: 12, fontWeight: 600, padding: '3px 9px', borderRadius: 999, background: s.bg, color: s.ink }}>{s.label}</span>;
}

function providerName(p) {
  return p === 'hubspot' ? 'HubSpot' : p === 'salesforce' ? 'Salesforce' : (p || 'CRM');
}

// Only ever follow an https URL the server built. Belt-and-braces: the
// adapters already refuse anything else, and this makes a bad value
// render as plain text here too rather than becoming a live href.
function safeHref(url) {
  return typeof url === 'string' && /^https:\/\//i.test(url) ? url : null;
}

/**
 * A record's name, hyperlinked into the CRM when we have a URL for it
 * and plain text when we don't. The arrow marks it as leaving
 * TrailerCast, so nobody clicks expecting an in-app panel.
 */
function CrmRecordLink({ url, children, title, style }) {
  const href = safeHref(url);
  if (!href) return <span style={style}>{children}</span>;
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      title={title}
      style={{ color: 'inherit', textDecoration: 'none', borderBottom: '1px solid currentColor', ...style }}
    >
      {children}
      <span aria-hidden="true" style={{ marginLeft: 4, opacity: 0.7, fontSize: '0.85em' }}>↗</span>
    </a>
  );
}

export default function RoomCrmPanel({ authToken, spaceId, onPeopleChanged }) {
  const [state, setState] = useState(null); // { connected, provider, needsReauth, link, history }
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [suggestions, setSuggestions] = useState(null);
  const [q, setQ] = useState('');
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [contacts, setContacts] = useState(null);
  const [contactsError, setContactsError] = useState(null);

  const base = `/api/buyer-spaces/${spaceId}/crm`;

  const load = useCallback(async () => {
    try {
      const r = await apiRequest(base, { token: authToken });
      setState(r);
      setError(null);
    } catch (err) {
      setError(err.message);
    }
  }, [authToken, base]);

  useEffect(() => { load(); }, [load]);

  // Suggestions are one CRM call per buyer email; only ask when the
  // room is unlinked and the CRM is reachable.
  useEffect(() => {
    if (!state || !state.connected || state.needsReauth || state.link) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await apiRequest(`${base}/suggestions`, { token: authToken });
        if (!cancelled) setSuggestions(r.suggestions || []);
      } catch (err) {
        if (!cancelled) { setSuggestions([]); setError(err.message); }
      }
    })();
    return () => { cancelled = true; };
  }, [state, authToken, base]);

  const search = async (e) => {
    e.preventDefault();
    if (q.trim().length < 2) return;
    setSearching(true);
    setError(null);
    try {
      const r = await apiRequest(`${base}/search?q=${encodeURIComponent(q.trim())}`, { token: authToken });
      setResults(r.results || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setSearching(false);
    }
  };

  const link = async (deal, matchedVia) => {
    setBusy(true);
    setError(null);
    try {
      await apiRequest(`${base}/link`, { token: authToken, method: 'POST', body: { dealId: deal.dealId, dealName: deal.dealName || null, matchedVia } });
      setResults(null); setQ(''); setSuggestions(null); setContacts(null);
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const unlink = async () => {
    if (!window.confirm('Unlink this deal from the room? The stage history stays; you can link a deal again later.')) return;
    setBusy(true);
    setError(null);
    try {
      await apiRequest(`${base}/link`, { token: authToken, method: 'DELETE' });
      setContacts(null);
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const loadContacts = async () => {
    setContactsError(null);
    try {
      const r = await apiRequest(`${base}/contacts`, { token: authToken });
      setContacts(r.contacts || []);
    } catch (err) {
      setContactsError(err.message);
      setContacts([]);
    }
  };

  const addToPeople = async (c) => {
    setBusy(true);
    try {
      await apiRequest(`/api/buyer-spaces/${spaceId}/people`, {
        token: authToken, method: 'POST',
        body: { side: 'buyer', fullName: c.name || c.email, email: c.email || null, title: c.title || null, role: c.role || null },
      });
      setContacts((prev) => (prev || []).map((x) => (x.contactId === c.contactId ? { ...x, alreadyOnPeople: true } : x)));
      if (typeof onPeopleChanged === 'function') onPeopleChanged();
    } catch (err) {
      setContactsError(err.message);
    } finally {
      setBusy(false);
    }
  };

  if (error && !state) return <div role="alert" style={alertStyle}>{error}</div>;
  if (!state) return <div style={{ color: 'var(--room-text-muted, #64748b)' }}>Loading…</div>;

  if (!state.connected) {
    return (
      <div style={cardStyle}>
        <p style={{ margin: 0, color: 'var(--room-text-body, #334155)' }}>
          No CRM is connected for your workspace. An admin can connect HubSpot or Salesforce under <strong>Integrations → CRM</strong>; deals become linkable here right after.
        </p>
      </div>
    );
  }

  const { link: dealLink, history, provider, needsReauth } = state;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {needsReauth && (
        <div role="alert" style={{ ...alertStyle, background: '#fef3c7', borderColor: '#fcd34d', color: '#92400e' }}>
          The {providerName(provider)} connection needs to be reconnected under Integrations before this panel can read or link deals.
        </div>
      )}
      {error && <div role="alert" style={alertStyle}>{error}</div>}

      {dealLink ? (
        <>
          <div style={cardStyle}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 220 }}>
                <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--room-text-subtle, #94a3b8)' }}>{providerName(dealLink.provider)} deal</div>
                <div style={{ fontSize: 18, fontWeight: 700, margin: '2px 0 6px' }}>
                  <CrmRecordLink url={dealLink.dealUrl} title={`Open this deal in ${providerName(dealLink.provider)}`}>
                    {dealLink.dealName || dealLink.crmDealId}
                  </CrmRecordLink>
                </div>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', fontSize: 13, color: 'var(--room-text-muted, #64748b)' }}>
                  <DealStatus status={dealLink.dealStatus} />
                  {dealLink.stageName && <span>Stage: <strong style={{ color: 'var(--room-text-body, #334155)' }}>{dealLink.stageName}</strong></span>}
                  {dealLink.dealValueCents != null && <span>Value: <strong style={{ color: 'var(--room-text-body, #334155)' }}>{moneyLabel(dealLink.dealValueCents)}</strong></span>}
                </div>
                <div style={{ fontSize: 12, color: 'var(--room-text-subtle, #94a3b8)', marginTop: 8 }}>
                  {dealLink.level === 'demo' ? 'Linked through a recording in this room' : `Linked ${dealLink.matchedVia === 'manual' ? 'by hand' : 'by email match'}`}
                  {dealLink.lastSyncedAt ? ` · synced ${formatWhen(dealLink.lastSyncedAt)}` : ''}
                </div>
              </div>
              {dealLink.level === 'room' && (
                <button type="button" onClick={unlink} disabled={busy || needsReauth} style={{ ...ghostBtn, color: '#dc2626' }}>Unlink</button>
              )}
            </div>
          </div>

          {history && history.length > 0 && (
            <div style={cardStyle}>
              <div style={{ fontWeight: 600, marginBottom: 8 }}>Stage history</div>
              <ol style={{ margin: 0, paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13, color: 'var(--room-text-body, #334155)' }}>
                {history.map((h, i) => (
                  <li key={i}>
                    <strong>{h.stageName || h.dealStatus || '—'}</strong>
                    <span style={{ color: 'var(--room-text-subtle, #94a3b8)' }}> · {formatWhen(h.recordedAt)}{h.dealValueCents != null ? ` · ${moneyLabel(h.dealValueCents)}` : ''}</span>
                  </li>
                ))}
              </ol>
            </div>
          )}

          <div style={cardStyle}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: contacts ? 10 : 0 }}>
              <div style={{ fontWeight: 600 }}>Contacts on this deal</div>
              <button type="button" onClick={loadContacts} disabled={busy || needsReauth} style={ghostBtn}>{contacts ? 'Refresh' : 'Load from CRM'}</button>
            </div>
            {contactsError && <div role="alert" style={{ ...alertStyle, marginTop: 8 }}>{contactsError}</div>}
            {contacts && contacts.length === 0 && !contactsError && <div style={{ fontSize: 13, color: 'var(--room-text-muted, #64748b)' }}>No contacts on the deal.</div>}
            {contacts && contacts.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {contacts.map((c) => (
                  <div key={c.contactId} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', borderRadius: 10, background: 'var(--room-surface, #f8fafc)' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: 14 }}>
                        <CrmRecordLink url={c.contactUrl} title={`Open this contact in ${providerName(provider)}`}>
                          {c.name || c.email || 'Unnamed contact'}
                        </CrmRecordLink>
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--room-text-muted, #64748b)', overflow: 'hidden', textOverflow: 'ellipsis' }}>{[c.title, c.email].filter(Boolean).join(' · ')}</div>
                    </div>
                    {c.alreadyOnPeople ? (
                      <span style={{ fontSize: 12, color: 'var(--room-text-subtle, #94a3b8)' }}>On People</span>
                    ) : (
                      <button type="button" onClick={() => addToPeople(c)} disabled={busy} style={ghostBtn}>Add to People</button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      ) : (
        <>
          <div style={cardStyle}>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>Suggested from the buyer emails in this room</div>
            {suggestions === null ? (
              <div style={{ fontSize: 13, color: 'var(--room-text-muted, #64748b)' }}>{needsReauth ? 'Reconnect the CRM to see suggestions.' : 'Checking the CRM…'}</div>
            ) : suggestions.length === 0 ? (
              <div style={{ fontSize: 13, color: 'var(--room-text-muted, #64748b)' }}>No deal matched the buyer emails on the People tab. Search below.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8 }}>
                {suggestions.map((d) => <DealRow key={d.dealId} deal={d} note={`matched ${d.matchedEmail}`} onLink={() => link(d, 'suggestion')} busy={busy} />)}
              </div>
            )}
          </div>

          <form onSubmit={search} style={cardStyle}>
            <label htmlFor="crm-deal-search" style={{ fontWeight: 600, display: 'block', marginBottom: 8 }}>Search {providerName(provider)} deals</label>
            <div style={{ display: 'flex', gap: 8 }}>
              <input id="crm-deal-search" type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Deal name" style={inputStyle} disabled={needsReauth} />
              <button type="submit" disabled={searching || needsReauth || q.trim().length < 2} style={ghostBtn}>{searching ? 'Searching…' : 'Search'}</button>
            </div>
            {results && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 10 }}>
                {results.length === 0 ? (
                  <div style={{ fontSize: 13, color: 'var(--room-text-muted, #64748b)' }}>No deals matched.</div>
                ) : results.map((d) => <DealRow key={d.dealId} deal={d} onLink={() => link(d, 'manual')} busy={busy} />)}
              </div>
            )}
          </form>
        </>
      )}
    </div>
  );
}

function DealRow({ deal, note, onLink, busy }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', borderRadius: 10, background: 'var(--room-surface, #f8fafc)' }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600, fontSize: 14 }}>
          <CrmRecordLink url={deal.dealUrl} title="Open this deal in the CRM to check it before linking">
            {deal.dealName || deal.dealId}
          </CrmRecordLink>
        </div>
        <div style={{ fontSize: 12, color: 'var(--room-text-muted, #64748b)', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <DealStatus status={deal.dealStatus} />
          {deal.stageName && <span>{deal.stageName}</span>}
          {deal.dealValueCents != null && <span>{moneyLabel(deal.dealValueCents)}</span>}
          {note && <span>· {note}</span>}
        </div>
      </div>
      <button type="button" onClick={onLink} disabled={busy} style={primaryBtnSmall}>Link</button>
    </div>
  );
}

const cardStyle = { background: 'var(--room-card, #fff)', border: '1px solid var(--room-border, #e2e8f0)', borderRadius: 14, padding: 16, color: 'var(--room-text-strong, #0f172a)' };
const inputStyle = { flex: 1, boxSizing: 'border-box', padding: '9px 11px', borderRadius: 10, border: '1px solid var(--room-border, #e2e8f0)', background: 'var(--room-card, #fff)', color: 'var(--room-text-strong, #0f172a)', fontSize: 14, fontFamily: 'inherit' };
const ghostBtn = { padding: '8px 14px', borderRadius: 10, border: '1px solid var(--room-border, #e2e8f0)', background: 'var(--room-card, #fff)', color: 'var(--room-text-body, #334155)', fontFamily: 'inherit', fontSize: 13, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' };
const primaryBtnSmall = { padding: '7px 14px', borderRadius: 10, border: '1px solid #7c3aed', background: '#7c3aed', color: '#fff', fontFamily: 'inherit', fontSize: 13, fontWeight: 600, cursor: 'pointer' };
const alertStyle = { padding: '10px 12px', borderRadius: 10, background: 'rgba(239, 68, 68, 0.08)', border: '1px solid rgba(239, 68, 68, 0.3)', color: '#dc2626', fontSize: 14 };
